import { addMonths, subYears } from "date-fns";
import store from "@/store";
import { Employee } from "@/models/employees";
import { type UserApiData } from "@/modules/security/models/user-model";
import { UserPut } from "@/api/models/UserPut";
import { roleEnumEnum } from "@/api/models/RoleEnum";

/**
 * Composable for user-related operations and calculations
 * Provides functions that work with UserApiData to maintain separation from class-based model
 */
export function useUser() {
  /**
   * Gets the start date for employments query (1 year ago)
   */
  const employmentsFrom = (): Date => {
    return subYears(new Date(), 1);
  };

  /**
   * Gets the end date for employments query based on current employment and future employment
   * @param userData - The user API data
   * @param toDate - The target end date
   */
  const employmentsTo = (userData: UserApiData, toDate: Date): Date => {
    const oneMonthFromToday = addMonths(new Date(), 1);
    const employee = getEmployee(userData);
    const currentEmployment = employee?.employment(new Date());

    if (currentEmployment && currentEmployment.end_date < toDate) {
      const employmentOneMonthFromNow = employee?.employment(oneMonthFromToday);
      if (employmentOneMonthFromNow) {
        return employmentOneMonthFromNow.end_date;
      } else {
        return currentEmployment.end_date;
      }
    } else {
      return toDate > oneMonthFromToday ? toDate : oneMonthFromToday;
    }
  };

  /**
   * Checks if the user is a manager
   * @param userData - The user API data
   */
  const isManager = (userData: UserApiData): boolean => {
    return userData.selected_institution?.role === roleEnumEnum.Manager;
  };

  /**
   * Checks if the user is an employee
   * @param userData - The user API data
   */
  const isEmployee = (userData: UserApiData): boolean => {
    return userData.selected_institution?.role === roleEnumEnum.Employee;
  };

  /**
   * Gets the employee data for the user
   * @param userData - The user API data
   */
  const getEmployee = (userData: UserApiData): Employee | undefined => {
    const employee = store.getters["employees/GetEmployeeById"](userData.employee_id);
    return employee;
  };

  /**
   * Creates a short name from a full name (initials)
   * @param name - The full name to convert
   */
  const shortname = (name: string): string => {
    return name
      ? Array.prototype.map
      // eslint-disable-next-line space-before-function-paren
        .call(name.split(" "), function (x: string): string {
          return x.substring(0, 1).toUpperCase();
        })
        .join("")
      : "";
  };

  /**
   * Converts user data to PUT object for API updates
   * @param userData - The user API data
   */
  const convertToPutObject = (userData: UserApiData): UserPut => {
    const u: UserPut = {
      id: userData.id ?? -1,
      email_address: userData.email_address ?? ""
    };
    return u;
  };

  return {
    // Employment functions
    employmentsFrom,
    employmentsTo,

    // Role checking functions
    isManager,
    isEmployee,

    // Utility functions
    getEmployee,
    shortname,
    convertToPutObject
  };
}
